import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Modal from '@mui/material/Modal'
import { useModalContext } from '../../../contexts/ModalContext'
import TextField from '@mui/material/TextField'
import { Container, FormContainer } from './styles'
import { getOfferDiscountPercent, getOfferMechanicName } from '../offerDisplay'
import * as OffersService from '../../../services/mechanicActivityOffers'
import * as MechanicsService from '../../../services/mechanics'
import Button from '@mui/material/Button'
import { Typography, Chip, Divider, Alert } from '@mui/material'

const StatusLabel = {
    APPROVED: { text: 'APROVADO', color: '#2e7d32' },
    PENDING: { text: 'PENDENTE', color: '#1565c0' },
    DENIED: { text: 'NEGADO', color: '#c62828' }
}

function formatDateOnly(dateStr) {
    if (!dateStr) return '—'
    const datePart = String(dateStr).slice(0, 10)
    const [y, m, d] = datePart.split('-').map(Number)
    if (!y || !m || !d) return dateStr
    return new Date(y, m - 1, d).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    })
}

function todayIsoLocal() {
    const now = new Date()
    const y = now.getFullYear()
    const m = String(now.getMonth() + 1).padStart(2, '0')
    const d = String(now.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
}

function isValidUntilBeforeToday(validUntil) {
    if (!validUntil) return false
    const datePart = String(validUntil).slice(0, 10)
    return datePart < todayIsoLocal()
}

function apiErrorMessage(err, fallback) {
    const data = err?.response?.data
    if (typeof data === 'string') return data
    if (data?.message) return data.message
    if (Array.isArray(data?.errors) && data.errors.length) return data.errors.join(' ')
    if (data?.error) return data.error
    return fallback
}

const ApproveOrDenyModal = ({ updateTable, offer }) => {
    const { setModal } = useModalContext()
    const [reason, setReason] = useState(offer.reason ?? '')
    const [resolvedMechanicName, setResolvedMechanicName] = useState(() => getOfferMechanicName(offer))
    const [mechanicLookupFinished, setMechanicLookupFinished] = useState(() => !!getOfferMechanicName(offer))

    useEffect(() => {
        const fromDto = getOfferMechanicName(offer)
        if (fromDto) {
            setResolvedMechanicName(fromDto)
            setMechanicLookupFinished(true)
            return undefined
        }
        setMechanicLookupFinished(false)
        let cancelled = false
        const id = offer.mechanicId
        if (id == null) {
            setMechanicLookupFinished(true)
            return undefined
        }
        MechanicsService.getAllMechanics(0, 25, 'id', 'ASC', String(id))
            .then((res) => {
                const match = res.content?.find((m) => String(m.id) === String(id))
                if (!cancelled && match?.name) setResolvedMechanicName(match.name)
            })
            .catch(() => {})
            .finally(() => {
                if (!cancelled) setMechanicLookupFinished(true)
            })
        return () => {
            cancelled = true
        }
    }, [offer])

    const handleClose = () => setModal(null)

    const status = StatusLabel[offer.status] || { text: offer.status, color: 'gray' }
    const isPending = offer.status === 'PENDING'
    const isApproved = offer.status === 'APPROVED'
    const canDeny = isPending || isApproved
    const expiredForApproval = isPending && isValidUntilBeforeToday(offer.validUntil)

    const activityLabel = offer.activityName || offer.activityTitle || offer.catalogActivityName || null
    const discountPct = getOfferDiscountPercent(offer)

    const approve = async (event) => {
        event.preventDefault()

        await OffersService.approve({ id: offer.id })
            .then(() => {
                alert('Oferta aprovada!')
                setModal(null)
                updateTable()
            })
            .catch((err) => alert(apiErrorMessage(err, 'Erro ao aprovar esta oferta!')))
    }

    const deny = async (event) => {
        event.preventDefault()

        if (!reason.trim()) {
            alert('Informe o motivo da recusa.')
            return
        }

        await OffersService.deny({ id: offer.id, reason })
            .then(() => {
                alert(isApproved ? 'Oferta revogada (negada).' : 'Oferta negada!')
                setModal(null)
                updateTable()
            })
            .catch((err) => alert(apiErrorMessage(err, 'Erro ao negar esta oferta!')))
    }

    return (
        <Modal
            open={true}
            onClose={handleClose}
            aria-labelledby='modal-modal-title'
            aria-describedby='modal-modal-description'
        >
            <Container>
                <Box
                    m={3}
                    mb={1}
                    fontWeight='bold'
                    color='#333'
                    fontSize={{
                        xs: 'h7.fontSize',
                        sm: 'h7.fontSize',
                        md: 'h6.fontSize',
                        xl: 'h5.fontSize'
                    }}
                >
                    Gerenciar Oferta de Atividade
                </Box>

                <FormContainer>
                    <Box display='flex' alignItems='center' gap={2} mb={2} flexWrap='wrap'>
                        <Chip
                            label={status.text}
                            size='small'
                            sx={{ backgroundColor: status.color, color: 'white', fontWeight: 'bold' }}
                        />
                        <Typography variant='body2' color='textSecondary'>
                            Oferta #{offer.id}
                        </Typography>
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    <Box mb={2}>
                        <Typography variant='subtitle2' color='textSecondary'>
                            Estabelecimento
                        </Typography>
                        <Typography variant='body1' fontWeight='bold'>
                            {!mechanicLookupFinished
                                ? 'Carregando nome…'
                                : resolvedMechanicName || 'Nome não encontrado na busca'}
                        </Typography>
                        <Typography variant='body2' color='textSecondary'>
                            ID do estabelecimento: {offer.mechanicId}
                        </Typography>
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    <Box mb={2}>
                        <Typography variant='subtitle2' color='textSecondary'>
                            Atividade (catálogo)
                        </Typography>
                        <Typography variant='body1' fontWeight='bold'>
                            {activityLabel ? `${activityLabel} (ID ${offer.activityId})` : `ID ${offer.activityId}`}
                        </Typography>
                    </Box>

                    <Box mb={2}>
                        <Typography variant='subtitle2' color='textSecondary'>
                            Desconto
                        </Typography>
                        <Typography variant='body1'>{discountPct != null ? `${discountPct}%` : '—'}</Typography>
                    </Box>

                    <Box mb={2}>
                        <Typography variant='subtitle2' color='textSecondary'>
                            Válida até (inclusive)
                        </Typography>
                        <Typography variant='body1'>{formatDateOnly(offer.validUntil)}</Typography>
                    </Box>

                    {expiredForApproval && (
                        <Alert severity='warning' sx={{ mb: 2 }}>
                            A data de validade já passou. A API não permitirá aprovação até que a oferta seja atualizada
                            com uma nova data.
                        </Alert>
                    )}

                    {isApproved && (
                        <Alert severity='info' sx={{ mb: 2 }}>
                            Oferta aprovada: você ainda pode negar para revogar a publicação, se necessário.
                        </Alert>
                    )}

                    <Divider sx={{ mb: 2 }} />

                    {offer.status === 'DENIED' && offer.reason && (
                        <Box
                            mb={2}
                            sx={{ background: '#fff3f3', borderRadius: 1, p: 1.5, border: '1px solid #ffcdd2' }}
                        >
                            <Typography variant='subtitle2' color='error'>
                                Motivo da recusa
                            </Typography>
                            <Typography variant='body2'>{offer.reason}</Typography>
                        </Box>
                    )}

                    {canDeny && (
                        <TextField
                            name='reason'
                            label='Motivo (obrigatório para negar)'
                            value={reason}
                            variant='outlined'
                            style={{ width: '100%' }}
                            onChange={(event) => setReason(event.target.value)}
                            multiline
                            rows={4}
                        />
                    )}
                </FormContainer>

                <Box display='flex' justifyContent='flex-end' padding='16px 20px' gap='8px'>
                    {isPending && (
                        <Button
                            variant='contained'
                            sx={{
                                background: '#2e7d32',
                                '&:hover': { background: '#1b5e20' }
                            }}
                            onClick={(event) => approve(event)}
                        >
                            Aprovar
                        </Button>
                    )}
                    {canDeny && (
                        <Button
                            variant='contained'
                            sx={{
                                background: '#c62828',
                                '&:hover': { background: '#b71c1c' }
                            }}
                            onClick={(event) => deny(event)}
                        >
                            Negar
                        </Button>
                    )}
                    <Button variant='outlined' onClick={handleClose}>
                        Fechar
                    </Button>
                </Box>
            </Container>
        </Modal>
    )
}

export default ApproveOrDenyModal
